// D1 read-replication bookmark echo (Quickback `readReplication: true`).
//
// The backend runs feature-DB reads on a D1 Sessions API session and returns
// the session's latest bookmark on EVERY response as `x-d1-bookmark`. A request
// that sends `x-d1-bookmark` continues from at least that database state, so a
// GET after our own write sees the write (read-your-writes) and never moves
// backwards. Without it a GET is served by the nearest replica and may be
// slightly stale (create a guest → the guests list misses it). Writes always
// start on the primary; a bad/stale bookmark is ignored server-side, so sending
// one can never fail a request.
//
// ONE bookmark per browser tab, held in memory only (module scope — never
// localStorage). Newest wins: Cloudflare documents D1 bookmarks as
// lexicographically sortable, oldest→newest ("Bookmarks are lexicographically
// sortable. Sorting orders a list of bookmarks from oldest-to-newest." —
// https://developers.cloudflare.com/glossary/ , D1 "bookmark"; see also
// https://developers.cloudflare.com/d1/reference/time-travel/#bookmarks). So we
// keep the string max, and a slow response carrying an older bookmark can't
// move us backwards.
//
// SSR: `apiFetch` does NOT send or record a bookmark when running on the
// server. The console's worker serves many users from one isolate, so a
// module-level bookmark there would leak one user's session position into
// another's request; and SSR only does a single cookie-forwarded get-session
// per request (lib/auth-guard.ts), so there's no earlier write in that request
// to be consistent with. The browser picks up a bookmark on its first call.
//
// This file deliberately has no imports (no `import.meta.env`) so the node test
// runner can load it directly.

export const D1_BOOKMARK_HEADER = "x-d1-bookmark"

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>

/** Newest-wins bookmark holder. Exported as a class so tests get a fresh one. */
export class D1BookmarkStore {
  private current: string | null = null

  get(): string | null {
    return this.current
  }

  /** Record a bookmark seen on a response; keeps the lexicographic max. */
  note(value: string | null | undefined): void {
    const v = value?.trim()
    if (!v) return
    if (this.current === null || v > this.current) this.current = v
  }

  clear(): void {
    this.current = null
  }
}

/**
 * Wrap a fetch so every request carries the store's bookmark (unless the
 * caller set the header explicitly) and every response's bookmark is recorded.
 */
export function withD1Bookmark(fetchImpl: FetchLike, store: D1BookmarkStore): FetchLike {
  return async (input, init) => {
    const bookmark = store.get()
    let nextInit = init
    if (bookmark) {
      const base =
        init?.headers ??
        (typeof Request !== "undefined" && input instanceof Request ? input.headers : undefined)
      const headers = new Headers(base)
      if (!headers.has(D1_BOOKMARK_HEADER)) headers.set(D1_BOOKMARK_HEADER, bookmark)
      nextInit = { ...init, headers }
    }
    const res = await fetchImpl(input, nextInit)
    store.note(res.headers.get(D1_BOOKMARK_HEADER))
    return res
  }
}

/** The tab-wide store. Browser-only in practice (see SSR note above). */
export const d1Bookmarks = new D1BookmarkStore()

const browserFetch = withD1Bookmark((input, init) => fetch(input, init), d1Bookmarks)

/**
 * `fetch` for requests to the Attend API (anything built with `backendUrl()`).
 * In the browser it echoes the D1 bookmark; on the server it is plain fetch.
 * Do NOT use for third-party hosts (e.g. presigned R2 upload URLs).
 */
export function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  if (typeof window === "undefined") return fetch(input, init)
  return browserFetch(input, init)
}

/** Forget the bookmark (sign-out / user switch). */
export function clearD1Bookmark(): void {
  d1Bookmarks.clear()
}
